/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Node.js SDK", description = "Hightouch's Node.js SDK for collecting events", lastUpdatedDate = new Date(1695254400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Node.js SDK",
        description: "Hightouch's Node.js SDK for collecting events",
        lastUpdatedDate: "Thu Sep 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      pre: "pre",
      code: "code",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      h3: "h3",
      strong: "strong"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Node.js SDK makes it easy to track events from Node.js applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsx(_components.p, {
        children: "The Node.js SDK is available via NPM."
      }), "\n", _jsx(_components.p, {
        children: "To install the Node.js SDK, run:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "npm install @ht-sdks/events-sdk-node\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "Then, initialize the SDK wherever you want to track events:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["const ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Analytics"
          }), " = require(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'@ht-sdks/events-sdk-node'"
          }), ")", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n\nconst ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "client"
          }), " = new Analytics(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"HT_WRITE_KEY\""
          }), ", {\n  dataPlaneUrl: \"https://us-east-1.hightouch-events.com\",\n})", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "Analytics"
        }), " constructor accepts the following parameters:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushAt"
              })
            }), _jsx(_components.td, {
              children: "Integer"
            }), _jsx(_components.td, {
              children: "The maximum number of events to store locally before forwarding to Hightouch. Defaults to 20."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushInterval"
              })
            }), _jsx(_components.td, {
              children: "Integer"
            }), _jsx(_components.td, {
              children: "The maximum amount of time (in milliseconds) to store events locally before forwarding to Hightouch. Defaults to 10000."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "maxInternalQueueSize"
              })
            }), _jsx(_components.td, {
              children: "Integer"
            }), _jsxs(_components.td, {
              children: ["The maximum number of events to store locally. This may be configured separately from ", _jsx(_components.code, {
                children: "flushAt"
              }), " because the event requests to Hightouch may fail, in which case they remain on the queue so that they can be retried."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "logLevel"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The log level for internal logs. Can be set to ", _jsx(_components.code, {
                children: "log"
              }), ", ", _jsx(_components.code, {
                children: "info"
              }), ", ", _jsx(_components.code, {
                children: "debug"
              }), ", ", _jsx(_components.code, {
                children: "warn"
              }), ", ", _jsx(_components.code, {
                children: "error"
              }), ", or ", _jsx(_components.code, {
                children: "none"
              }), ". Defaults to ", _jsx(_components.code, {
                children: "info"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "dataPlaneUrl"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The URL to send events to. Defaults to ", _jsx(_components.a, {
                href: "https://us-east-1.hightouch-events.com",
                children: "https://us-east-1.hightouch-events.com"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["Unlike the ", _jsx(_components.a, {
          href: "./browser",
          children: "Browser SDK"
        }), ", it does ", _jsx(_components.strong, {
          children: "not"
        }), " persist the user ID and\ntraits locally, so user IDs must be explicitly added to other events. This is\nbecause server side events are usually servicing many different users at once."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "identify"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "traits"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "location"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  }\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the identify event to be sent. The second argument is an optional callback that's invoked after the event is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["client", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".identify"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method sends a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "track"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "event"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Order completed\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "total"
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "29.99"
          }), ",\n  }\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the track event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["client", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".track"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "page"
        }), " method sends a ", _jsx(_components.code, {
          children: "page"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "page"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Docs\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the page event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["client", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".page"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "screen"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Docs\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the screen event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["client", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".screen"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsx(_components.td, {
              children: "Additional properties about the screen."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "group"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "groupId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"456\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "traits"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "company_location"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  },\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the group event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), "(message, [callback])\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      })]
    });
  }
}
export default MDXContent;
